import { AddIcon, SearchIcon, SmallCloseIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Grid,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import React, { useMemo, useRef, useState } from 'react';

import { TapeBuilderUI } from '@finley-technologies/tape-builder';

import { NewNodeDisplay } from './commonNodeDisplay';
import { generatedNodes, operationNodes, rawNodes } from './nodes';

type NodeCategory = {
  heading: string;
  nodes: TapeBuilderUI.TapeBuilderNodeType[];
};

const nodeCategories: NodeCategory[] = [
  { heading: 'Raw Tapes', nodes: rawNodes },
  { heading: 'Operations', nodes: operationNodes },
  { heading: 'Generated Tapes', nodes: generatedNodes },
];

// A node matches when every search term appears in its display text, node type,
// or any of its badge labels. Splitting on whitespace makes the search an AND
// over terms (e.g. "raw loan" matches "Raw LoanTape").
const nodeMatchesTerms = (
  nodeType: TapeBuilderUI.TapeBuilderNodeType,
  terms: string[],
): boolean => {
  if (terms.length === 0) {
    return true;
  }
  const uiNode = TapeBuilderUI.getUINodeByType(nodeType);
  const haystack = [uiNode.nodeTypeText, nodeType, ...uiNode.badgeTexts]
    .join(' ')
    .toLowerCase();
  return terms.every((term) => haystack.includes(term));
};

// Searchable palette for adding nodes to the canvas. Users type to narrow the
// list, then drag a node onto the graph to create it (drag wiring lives in
// NewNodeDisplay -> ReactFlowWrapperControl).
export const NodeSearchPalette = (): React.ReactElement => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const terms = useMemo(
    () => query.trim().toLowerCase().split(/\s+/).filter(Boolean),
    [query],
  );

  const filteredCategories = useMemo(
    () =>
      nodeCategories
        .map((category) => ({
          ...category,
          nodes: category.nodes.filter((nodeType) =>
            nodeMatchesTerms(nodeType, terms),
          ),
        }))
        .filter((category) => category.nodes.length > 0),
    [terms],
  );

  const totalMatches = filteredCategories.reduce(
    (sum, category) => sum + category.nodes.length,
    0,
  );

  const handleClose = (): void => {
    setQuery('');
    onClose();
  };

  return (
    <Popover
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={handleClose}
      placement="bottom-start"
      initialFocusRef={inputRef}
      isLazy
    >
      <PopoverTrigger>
        <Button
          size="sm"
          variant="ghost"
          fontWeight="normal"
          leftIcon={<AddIcon boxSize={2.5} />}
        >
          Add Node
        </Button>
      </PopoverTrigger>
      <PopoverContent width="560px" maxW="90vw">
        <PopoverArrow />
        <PopoverHeader border="0" pb={1}>
          <InputGroup size="sm">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.400" />
            </InputLeftElement>
            <Input
              ref={inputRef}
              value={query}
              placeholder="Search nodes to add…"
              onChange={(event) => setQuery(event.target.value)}
              autoComplete="off"
            />
            {query.length > 0 && (
              <InputRightElement>
                <IconButton
                  aria-label="Clear search"
                  size="xs"
                  variant="ghost"
                  icon={<SmallCloseIcon />}
                  onClick={() => setQuery('')}
                />
              </InputRightElement>
            )}
          </InputGroup>
          <Text mt={2} fontSize="xs" color="gray.500">
            Drag a node onto the canvas to add it.
          </Text>
        </PopoverHeader>
        <PopoverBody maxH="60vh" overflowY="auto">
          {totalMatches === 0 ? (
            <Text py={6} textAlign="center" color="gray.500" fontSize="sm">
              No nodes match “{query}”.
            </Text>
          ) : (
            <VStack align="stretch" spacing={4}>
              {filteredCategories.map((category) => (
                <Box key={category.heading}>
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    textTransform="uppercase"
                    letterSpacing="wide"
                    color="gray.500"
                    mb={2}
                  >
                    {category.heading}
                  </Text>
                  <Grid
                    templateColumns="repeat(auto-fill, minmax(150px, 1fr))"
                    gap={3}
                    justifyItems="center"
                  >
                    {category.nodes.map((nodeType) => {
                      const uiNode = TapeBuilderUI.getUINodeByType(nodeType);
                      return (
                        <NewNodeDisplay
                          key={`palette-${nodeType}`}
                          nodeText={uiNode.nodeTypeText}
                          nodeType={nodeType}
                        />
                      );
                    })}
                  </Grid>
                </Box>
              ))}
            </VStack>
          )}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
